import { getNavigationUrls } from '@/store/config/actions';
import { Button, Menu, MenuButton, MenuList, MenuItem, useDisclosure, Box, Flex, Link } from '@chakra-ui/react';
import { defaultApplyUniConfig, HIGHEST_QUALIFICATIONS_MAP } from 'data';
import { useEffect, useMemo, JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Agent,
  AgentApplicantAuthorisationStatus,
  ApplicantOfferConditionType,
  ApplicantOfferRequirementTask,
  ApplicantOfferRequirementTaskLevel,
  ApplicantOfferRequirementTaskStatus,
  ApplicantOfferStatus,
  ApplicantStatusReason,
  ApplicationStatus,
  FeeStatus,
} from 'types';
import { getAllTasksFromRequirements } from '@/utilities/transformRequirements';
import { useApplicantSidebarActions } from './useApplicantSidebarActions';
import {
  reinstateApplicantOffer,
  requestTasks,
  unlinkApplicantEmail,
  updateApplicantOfferStatus,
} from '../../../store/apply/actions';
import { SidebarItem } from '../studentSidebar/SidebarItem';
import { ApplicationState } from './../../../store';
import { IssueOfferModal } from './modals/IssueOfferModal';
import { ReinstateOfferModal } from './modals/ReinstateOfferModal';
import { RequestTasksModal } from './modals/RequestTasksModal';
import { WithdrawRejectModal } from './modals/WithdrawRejectModal';
import { UnlinkEmailModal } from './modals/UnlinkEmailModal';
import { deriveOfferStatus } from './deriveOfferStatus';
import {
  CustomHeading,
  formatDate,
  modifyStartTextConditionType,
  parseDate,
  RoundBadgeWithTooltip,
} from 'shared-components';
import { getRole } from '@/store/selectors';
import { hasPerm, P } from '@/utilities/roles/permissions';
import { SidebarGroup } from '../studentActions/Group';
import { OfferTimeline } from '../OfferTimeline/OfferTimeline';
import { useTranslation } from "react-i18next";

export const ApplicationSidebar = (): JSX.Element => {
    const { t } = useTranslation('navigation');
  const dispatch = useDispatch();
  const params = useParams();
  const { offerId, userId, uni, applicationId } = params;
  const { isOpen: isIssueOfferOpen, onOpen: onIssueOfferOpen, onClose: onIssueOfferClose } = useDisclosure();
  const { isOpen: showMoreFields, onToggle: toggleShowMoreFields } = useDisclosure();

  const {
    isOpen: isWithdrawRejectOpen,
    onOpen: onWithdrawRejectOpen,
    onClose: onWithdrawRejectClose,
  } = useDisclosure();

  const {
    isOpen: isReinstateOfferOpen,
    onOpen: onReinstateOfferOpen,
    onClose: onReinstateOfferClose,
  } = useDisclosure();

  const {
    isOpen: isRequestPreOfferTasksOpen,
    onOpen: onRequestPreOfferTasksOpen,
    onClose: onRequestPreOfferTasksClose,
  } = useDisclosure();

  const {
    isOpen: isRequestEnrolmentTasksOpen,
    onOpen: onRequestEnrolmentTasksOpen,
    onClose: onRequestEnrolmentTasksClose,
  } = useDisclosure();

  const { applicant, application, offer, agentAccessStatus } = useSelector((state: ApplicationState) => state.apply);
  const { navigationUrls } = useSelector((state: ApplicationState) => state.config);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);
  const { nationality, dob, phoneNumber, email } = application;
  const programme = offer?.programme;

  const role = useSelector(getRole);

  // Get current application status to determine if it has been submitted or not, as this affects whether university users can edit details
  const isSubmitted = ![ApplicationStatus.InProgress, ApplicationStatus.Completed].includes(
    application.applicationStatus,
  );

  // Determine if the Edit button should be shown based on permissions and application status
  const canEditApplicantDetails = hasPerm(
    role,
    isSubmitted ? P.EDIT_APPLY_SUBMITTED_APPLICANT_DETAILS : P.EDIT_APPLY_APPLICANT_DETAILS,
  );

  const status = offer?.status;
  const requirements = offer?.requirements ?? [];
  const isConditional = status === ApplicantOfferStatus.Conditional;
  const isUnconditional = status === ApplicantOfferStatus.Unconditional;
  const isIssuedOffer = isConditional || isUnconditional;

  const isAuthorisationDenied = agentAccessStatus === AgentApplicantAuthorisationStatus.AUTHORISATION_DENIED;
  const isAuthorisationPending = agentAccessStatus === AgentApplicantAuthorisationStatus.AUTHORISATION_PENDING;

  // Extract all tasks from hierarchical requirements
  const allTasks = getAllTasksFromRequirements(requirements);

  const conditionReqs = allTasks.filter(task => {
    const effectiveLevel = task.pendingChanges?.level ?? task.level;
    return (
      (task.pendingChanges.status || task.status) === ApplicantOfferRequirementTaskStatus.Condition &&
      effectiveLevel !== ApplicantOfferRequirementTaskLevel.PreEnrolment &&
      task.type !== ApplicantOfferRequirementTask.StaffOnly
    );
  });
  const assessReqs = allTasks.filter(
    task =>
      (task.pendingChanges.status || task.status) === ApplicantOfferRequirementTaskStatus.Assess &&
      task.type !== ApplicantOfferRequirementTask.StaffOnly,
  );

  const feeStatus = application.feeStatus ?? applicant.feeStatus;

  const handleUnlinkEmailAddress = (): void => {
    dispatch(unlinkApplicantEmail({ uni, applicantId: userId }));
  };

  const { buttons, statusIndicators, unlinkEmailModalProps, isInOffersPath } = useApplicantSidebarActions({
    onIssueOfferOpen,
    onWithdrawRejectOpen,
    onReinstateOfferOpen,
    onRequestPreOfferTasksOpen,
    onRequestEnrolmentTasksOpen,
  });
  const actionButton = buttons.find(button => button.isPrimary);

  let fullName = '';
  if (application.givenName) {
    const middle = application.middleName ? ` ${application.middleName}` : '';
    fullName = `${application.givenName}${middle} ${application.familyName ?? ''}`;
  } else if (applicant.givenName) {
    const middle = applicant.middleName ? ` ${applicant.middleName}` : '';
    fullName = `${applicant.givenName}${middle} ${applicant.familyName ?? ''}`;
  } else {
    fullName = applicant.knownBy;
  }

  const handleIssueOfferConfirm = (startText?: string, endText?: string): void => {
    const offerStatus = deriveOfferStatus(requirements);

    dispatch(
      updateApplicantOfferStatus({
        applicantId: userId,
        uni,
        offerId,
        status: offerStatus,
        conditions: conditionReqs.map(req => req?.pendingChanges?.conditionText || req.conditionText),
        assess: assessReqs.map(req => req.taskText),
        applicationId: application.applicationId,
        startText,
        endText,
      }),
    );
  };

  const handleWithdrawRejectConfirm = (
    statusReason: ApplicantStatusReason,
    statusExplanationToApplicant: string,
    statusNote: string,
    notifyApplicant: boolean,
  ): void => {
    dispatch(
      updateApplicantOfferStatus({
        applicantId: userId,
        uni,
        offerId,
        status: isIssuedOffer ? ApplicantOfferStatus.Withdrawn : ApplicantOfferStatus.Rejected,
        statusReason,
        statusInternalNote: statusNote,
        statusExplanationToApplicant,
        notifyApplicant,
        applicationId: application.applicationId,
      }),
    );
  };

  const handleRequestPreOfferTasks = (): void => {
    dispatch(
      requestTasks({
        applicantId: userId,
        uni,
        offerId,
        applicationId: application.applicationId,
        level: ApplicantOfferRequirementTaskLevel.PreOffer,
      }),
    );
    onRequestPreOfferTasksClose();
  };

  const handleRequestEnrolmentTasks = (): void => {
    dispatch(
      requestTasks({
        applicantId: userId,
        uni,
        offerId,
        applicationId: application.applicationId,
        level: ApplicantOfferRequirementTaskLevel.PreEnrolment,
      }),
    );
    onRequestEnrolmentTasksClose();
  };

  const handleReinstateOffer = (): void => {
    dispatch(
      reinstateApplicantOffer({
        applicantId: userId,
        uni,
        offerId,
        applicationId: application.applicationId,
      }),
    );
    onReinstateOfferClose();
  };

  useEffect(() => {
    if (isIssueOfferOpen) {
      onIssueOfferClose();
    }
    if (isWithdrawRejectOpen) {
      onWithdrawRejectClose();
    }
  }, [offer?.reissueDate, offer?.status]);

  useEffect(() => {
    if (uni && applicant?.importedToCasShield && !navigationUrls) {
      // Offer Status Label and View Button - Only show when an offer has been issued
      dispatch(getNavigationUrls(uni));
    }
  }, [uni, applicant?.studentNo, applicant?.importedToCasShield, dispatch, navigationUrls]);

  const getHighestQualificationText = (code: string): string => {
    return HIGHEST_QUALIFICATIONS_MAP[code] ?? 'Not set';
  };

  // Resolve default offer letter text for the IssueOfferModal
  const resolvedOfferLetterText = useMemo(() => {
    const hasExistingOfferLetter = !!offer?.offerLetter?.startText || !!offer?.offerLetter?.endText;

    if (hasExistingOfferLetter) {
      const existingStart = offer?.pendingChanges?.offerLetter?.startText ?? offer?.offerLetter?.startText ?? '';
      const existingEnd = offer?.pendingChanges?.offerLetter?.endText ?? offer?.offerLetter?.endText ?? '';
      const isConditionalOffer = conditionReqs.length > 0;
      return {
        defaultStartText: modifyStartTextConditionType(existingStart, isConditionalOffer),
        defaultEndText: existingEnd,
      };
    }

    // First issue: resolve from templates client-side
    const defConfig = defaultApplyUniConfig;
    const startTemplate = applyConfig?.offerLetterStartTemplate ?? defConfig.offerLetterStartTemplate;
    const endTemplate = applyConfig?.offerLetterEndTemplate ?? defConfig.offerLetterEndTemplate;

    const derivedOfferStatus = deriveOfferStatus(requirements);
    const isConditionalOffer = conditionReqs.length > 0;
    const conditionType = isConditionalOffer
      ? ApplicantOfferConditionType.Conditional
      : ApplicantOfferConditionType.Unconditional;
    const conditionTypeWithArticle = isConditionalOffer ? `a ${conditionType}` : `an ${conditionType}`;

    const intakeIdLabel = offer?.intakeId ? formatIntakeLabel(offer.intakeId) : '';

    const templateData: Record<string, string> = {
      givenName: application.givenName ?? '',
      courseName: offer?.programme?.courseName ?? '',
      intakeIdLabel,
      conditionType,
      conditionTypeWithArticle,
      offerStatus: derivedOfferStatus,
    };

    const resolveTemplate = (template: string): string =>
      template.replace(/{(.*?)}/g, (_, key: string) => templateData[key] ?? `{${key}}`);

    return {
      defaultStartText: resolveTemplate(startTemplate),
      defaultEndText: resolveTemplate(endTemplate),
    };
  }, [
    offer?.offerLetter,
    offer?.pendingChanges,
    offer?.intakeId,
    offer?.programme,
    application.givenName,
    applyConfig,
    requirements,
    conditionReqs.length,
  ]);

  return (
    <>
      <SidebarGroup rowGap={2}>
        {statusIndicators.map((indicator, index) => (
          <Flex
            key={index}
            align="center"
            justify="space-between"
            data-selector={indicator.dataSelector}
            {...(indicator.contained && {
              bg: 'chakra-subtle-bg',
              borderRadius: 'lg',
              px: 3,
              py: 2,
            })}
          >
            <Flex align="center" gap={2}>
              <RoundBadgeWithTooltip
                icon={indicator.icon}
                label={indicator.label}
                colorScheme={indicator.colorScheme}
                size="xs"
                variant={indicator.variant ?? 'solid'}
                tooltipProps={{ label: undefined, children: null }}
              />
              <Box fontSize="sm" color={indicator.contained ? 'muted' : undefined}>
                {indicator.label}
              </Box>
            </Flex>
            {indicator.action && (
              <Link href={indicator.action.href} isExternal fontSize="sm">
                {indicator.action.label}
              </Link>
            )}
          </Flex>
        ))}

        {buttons.map(button => {
          if (button.isDropdown && button.menuItems) {
            return (
              <Menu key={button.label} matchWidth>
                <MenuButton as={Button} {...button.props}>
                  {button.label}
                </MenuButton>
                <MenuList>
                  {button.menuItems.map((item, itemIndex) => (
                    <MenuItem key={itemIndex} onClick={item.onClick}>
                      {item.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            );
          }
          return (
            <Button
              key={button.label}
              {...button.props}
              as={button.to ? RouterLink : undefined}
              to={button.to}
              onClick={button.onClick}
            >
              {button.label}
            </Button>
          );
        })}
      </SidebarGroup>

      <SidebarGroup
        header="Applicant"
        actions={
          canEditApplicantDetails && (
            <Button
              as={RouterLink}
              to="applicant-details"
              size="sm"
              variant="ghost"
              my={-2}
              data-selector="studentSummary-editButton"
            >
              Edit
            </Button>
          )
        }
      >
        <SidebarItem
          label={t('navigation:studentNo')}
          value={(application.studentNo || application['studentNumber']) ?? 'No number'}
          data-selector="studentSummary-studentNumber"
        />
        <SidebarItem label={t('navigation:feeStatus')} value={feeStatusLabels[feeStatus]} data-selector="studentSummary-feeStatus" />
        {[FeeStatus.NotSure, FeeStatus.Overseas].includes(feeStatus) && applicant.isVisaRequired !== undefined && (
          <SidebarItem
            label={t('navigation:studentVisa')}
            value={applicant.isVisaRequired ? 'Student visa required' : 'Student visa not required'}
            data-selector="studentSummary-visaStatus"
          />
        )}
        <SidebarItem
          label={t('navigation:nationality')}
          value={nationality ?? applicant.nationality}
          data-selector="studentSummary-nationality"
        />

        {/* Additional fields - conditionally collapsible on Offers page, always visible elsewhere */}
        {(isInOffersPath ? showMoreFields : true) && (
          <>
            <SidebarItem label={t('navigation:fullName')} value={fullName} data-selector="studentSummary-fullName" />
            <SidebarItem
              label={t('navigation:highestQualificationOnEntry')}
              value={getHighestQualificationText(application?.highestQualification?.code)}
              data-selector="studentSummary-highest-qualification"
            />
            <SidebarItem
              label={t('navigation:agent')}
              value={application.agentName ?? Agent.None}
              data-selector="studentSummary-agent"
            />
            {isAuthorisationDenied && (
              <SidebarItem
                value={'Authorisation denied'}
                data-selector="studentSummary-agent-authorisation-denied"
                valueColor="success"
                badgeProps={{ colorScheme: 'red', variant: 'solid' }}
                isBadge={true}
              />
            )}
            {isAuthorisationPending && (
              <SidebarItem
                value={'Pending authorisation'}
                data-selector="studentSummary-agent-authorisation-pending"
                valueColor="muted"
                isBadge={false}
              />
            )}
            <SidebarItem
              label={t('navigation:dateOfBirth')}
              value={formatDate(parseDate(dob ?? applicant.dob), 'DD MMM YYYY')}
              data-selector="studentSummary-dob"
            />
            <SidebarItem label={t('navigation:emailAddress')} value={email ?? applicant.email} data-selector="studentSummary-email" />
            <SidebarItem
              label={t('navigation:phoneNumber')}
              value={`+${phoneNumber ?? applicant.phoneNumber}`}
              data-selector="studentSummary-phoneNumber"
            />
          </>
        )}

        {/* Show more/less button - Only on Offers page */}
        {isInOffersPath && (
          <Box mt={2}>
            <Button size="sm" variant="link" onClick={toggleShowMoreFields} fontWeight="normal">
              {showMoreFields ? 'Show less' : 'Show more'}
            </Button>
          </Box>
        )}

        {/* TIMELINE Section - Only show on Offers page */}
        {isInOffersPath && (
          <Box mt={6}>
            <CustomHeading as="h3" level="h5" mb={0} isAllCaps>
              {t('navigation:timeline')}
                                      </CustomHeading>

            <OfferTimeline uni={uni} userId={userId} applicationId={applicationId} offerId={offerId} />
          </Box>
        )}
        {isIssueOfferOpen && offer && (
          <IssueOfferModal
            isOpen={isIssueOfferOpen}
            onClose={onIssueOfferClose}
            actionButtonLabel={actionButton?.label || 'Reissue offer'}
            offer={offer}
            programme={programme}
            conditionReqs={conditionReqs}
            onConfirm={handleIssueOfferConfirm}
            isLoading={offer.changeStatusLoading}
            defaultStartText={resolvedOfferLetterText.defaultStartText}
            defaultEndText={resolvedOfferLetterText.defaultEndText}
          />
        )}
        <WithdrawRejectModal
          isOpen={isWithdrawRejectOpen}
          onClose={onWithdrawRejectClose}
          isWithdraw={isIssuedOffer}
          onConfirm={handleWithdrawRejectConfirm}
          isLoading={offer?.changeStatusLoading}
        />
        <ReinstateOfferModal
          isOpen={isReinstateOfferOpen}
          onClose={onReinstateOfferClose}
          onConfirm={handleReinstateOffer}
          isLoading={false}
        />
        <RequestTasksModal
          isOpen={isRequestPreOfferTasksOpen}
          onClose={onRequestPreOfferTasksClose}
          onConfirm={handleRequestPreOfferTasks}
          isLoading={offer?.changeStatusLoading}
          title={t('navigation:requestPreOfferTasks')}
          description={t('navigation:onlyPreOfferTasksWillBeMadeAvail')}
        />
        <RequestTasksModal
          isOpen={isRequestEnrolmentTasksOpen}
          onClose={onRequestEnrolmentTasksClose}
          onConfirm={handleRequestEnrolmentTasks}
          isLoading={offer?.changeStatusLoading}
          title={t('navigation:requestEnrolmentTasks')}
          description={t('navigation:newEditedEnrolmentTasksWillBeReq')}
        />
        <UnlinkEmailModal
          isOpen={unlinkEmailModalProps.isOpen}
          onClose={unlinkEmailModalProps.onClose}
          onConfirm={handleUnlinkEmailAddress}
          email={email ?? applicant.email}
          isLoading={false}
        />
      </SidebarGroup>
    </>
  );
};

const feeStatusLabels: Record<FeeStatus, string> = {
  [FeeStatus.Home]: 'Home',
  [FeeStatus.Overseas]: 'Overseas',
  [FeeStatus.NotSure]: 'Not Sure',
};

function formatIntakeLabel(intakeId: string): string {
  const [year, month] = intakeId.split('-');
  if (!year || !month) return intakeId;
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}
